import React from 'react'
import { Box, Card, CardContent, Typography, alpha } from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'

interface MetricCardProps {
  title: string
  value: number | string
  subtitle?: string
  trend?: number // percentage change
  trendLabel?: string
  icon?: React.ReactNode
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'
  format?: 'number' | 'percentage' | 'currency'
}

const colorMap = {
  primary: { main: '#5c4b99', light: 'rgba(92, 75, 153, 0.1)' },
  secondary: { main: '#7c3aed', light: 'rgba(124, 58, 237, 0.1)' },
  success: { main: '#16a34a', light: 'rgba(22, 163, 74, 0.1)' },
  warning: { main: '#d97706', light: 'rgba(217, 119, 6, 0.1)' },
  error: { main: '#dc2626', light: 'rgba(220, 38, 38, 0.1)' },
  info: { main: '#0284c7', light: 'rgba(2, 132, 199, 0.1)' },
}

export default function MetricCard({
  title,
  value,
  subtitle,
  trend,
  trendLabel,
  icon,
  color = 'primary',
  format = 'number',
}: MetricCardProps) {
  const colors = colorMap[color]
  
  const formatValue = (val: number | string): string => {
    if (typeof val === 'string') return val
    switch (format) {
      case 'percentage':
        return `${val.toFixed(1)}%`
      case 'currency':
        return `${val.toLocaleString('da-DK')} kr.`
      default:
        return val.toLocaleString('da-DK')
    }
  }
  
  const getTrendIcon = () => {
    if (trend === undefined || trend === null) return null
    if (trend > 0) return <TrendingUpIcon sx={{ fontSize: 16 }} />
    if (trend < 0) return <TrendingDownIcon sx={{ fontSize: 16 }} />
    return <TrendingFlatIcon sx={{ fontSize: 16 }} />
  }
  
  const getTrendColor = () => {
    if (trend === undefined || trend === null) return 'text.secondary'
    if (trend > 0) return 'success.main'
    if (trend < 0) return 'error.main'
    return 'text.secondary'
  }

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.85rem',
            }}
          >
            {title}
          </Typography>
          {icon && (
            <Box
              sx={{
                backgroundColor: colors.light,
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.main,
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
        
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
          }}
        >
          {formatValue(value)}
        </Typography>
        
        {(trend !== undefined || subtitle || trendLabel) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {trend !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: getTrendColor(),
                    backgroundColor: alpha(
                      trend > 0 ? '#16a34a' : trend < 0 ? '#dc2626' : '#6b7280',
                      0.1
                    ),
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  {getTrendIcon()}
                  {trend > 0 ? '+' : ''}{trend}%
                </Box>
                {trendLabel && (
                  <Typography variant="caption" color="text.secondary">
                    {trendLabel}
                  </Typography>
                )}
              </Box>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
